'use client'

import { useCallback, useEffect, useRef, type Dispatch } from 'react'
import { playSoundEffect } from '@/hooks/use-santa-voice-player'
import type { VoiceRecording } from '@/hooks/use-voice-recorder'
import { restUntilFrom } from '@/lib/santa-chat/api'
import type { ChatAction, ChatEntry } from '@/lib/santa-chat/chat-state'

const SENT_SOUND = '/sounds/santa-chat-sent.mp3'
const RECEIVED_SOUND = '/sounds/santa-chat-received.mp3'

export type Outgoing = { inputMode: 'text'; text: string } | { inputMode: 'voice'; recording: VoiceRecording }

/** Lo enviado, tal como lo recibe `apply`: su burbuja local, el mensaje y lo que preparó el transporte. */
export interface SentMessage<Context> {
  localId: string
  outgoing: Outgoing
  context: Context
}

/**
 * Lo que cambia entre el chat del niño y el chat público al enviar. Todo lo
 * demás (la burbuja local, los sonidos, guardar lo enviado para reenviarlo y
 * la política común de errores) es de `useChatSender`.
 */
export interface ChatSendTransport<Result, Context = undefined> {
  /** Antes de que la burbuja entre en el historial (p. ej. el índice del mensaje para la analítica). */
  prepare(outgoing: Outgoing): Context
  request(outgoing: Outgoing): Promise<Result>
  /** Despacha el resultado y hace sonar a Papá Noel. */
  apply(result: Result, sent: SentMessage<Context>): void
  /**
   * Los errores propios del transporte. `'dropped'`: atendido y sin reenvío
   * posible; `'handled'`: atendido, se puede reenviar; `undefined`: la política
   * común (descanso si el servidor lo pide; si no, la burbuja queda fallida).
   */
  handleError?(error: unknown, localId: string): 'handled' | 'dropped' | undefined
  onResend?(outgoing: Outgoing): void
}

function localChildEntry(id: string, outgoing: Outgoing): ChatEntry {
  return {
    id,
    author: 'child',
    text: outgoing.inputMode === 'text' ? outgoing.text : '',
    inputMode: outgoing.inputMode,
    gesture: null,
    flagged: false,
    createdAt: new Date().toISOString(),
    delivery: 'sending',
    durationSeconds: outgoing.inputMode === 'voice' ? outgoing.recording.durationSeconds : undefined,
  }
}

/** La política común ante un error: descansar si el servidor lo pide; si no, la burbuja queda fallida. */
function defaultErrorPolicy(error: unknown, localId: string, dispatch: Dispatch<ChatAction>): void {
  const restUntil = restUntilFrom(error, new Date())
  if (restUntil !== null) dispatch({ type: 'rested', localId, until: restUntil })
  else dispatch({ type: 'turnFailed', localId })
}

/**
 * El envío de los dos chats con Papá Noel. Lo enviado se guarda hasta saber
 * cómo acabó: si falla por red, servidor o descanso, `resend` lo vuelve a
 * mandar tal cual. `send` y `resend` son estables (las burbujas memoizadas de
 * `SantaChatScreen` dependen de ello): leen el último transporte de un ref.
 */
export function useChatSender<Result, Context>(transport: ChatSendTransport<Result, Context>, dispatch: Dispatch<ChatAction>) {
  const transportRef = useRef(transport)
  useEffect(() => {
    transportRef.current = transport
  })
  const nextLocalId = useRef(0)
  const pending = useRef(new Map<string, Outgoing>())

  const send = useCallback(
    async (outgoing: Outgoing) => {
      const current = transportRef.current
      const localId = `local-${++nextLocalId.current}`
      const context = current.prepare(outgoing)
      pending.current.set(localId, outgoing)
      playSoundEffect(SENT_SOUND)
      dispatch({ type: 'childSent', entry: localChildEntry(localId, outgoing) })
      try {
        const result = await current.request(outgoing)
        pending.current.delete(localId)
        playSoundEffect(RECEIVED_SOUND)
        current.apply(result, { localId, outgoing, context })
      } catch (error) {
        const handled = current.handleError?.(error, localId)
        if (handled === 'dropped') pending.current.delete(localId)
        if (handled === undefined) defaultErrorPolicy(error, localId, dispatch)
      }
    },
    [dispatch]
  )

  const resend = useCallback(
    (localId: string) => {
      const outgoing = pending.current.get(localId)
      if (!outgoing) return
      pending.current.delete(localId)
      transportRef.current.onResend?.(outgoing)
      dispatch({ type: 'resendStarted', localId })
      void send(outgoing)
    },
    [dispatch, send]
  )

  return { send, resend }
}
